/**
 * @fileoverview Admission routes component with authentication guards.
 */

import React, {useCallback} from "react";
import {Route, useNavigate} from "react-router-dom";
import {SentryRoutes} from "../utils/sentryRoute";

import {PageWelcome} from "../pages/page-admission-welcome";
import {PageEnterAdmission} from "../pages/page-admission-enter";
import {PageAdmissionVerifyAnswers} from "../pages/page-admission-verify-answers";
import {PageAdmissionPrelude} from "../pages/page-admission-prelude";
import {PageAdmissionExperience} from "../pages/page-admission-experience";
import {PageExpiredSession} from "../pages/page-admission-expired-session";

import {UserIsLoggedOut} from "../models/admission-session/UserIsLoggedOut";
import {UserHasAdmissionEntryToken} from "../models/admission-session/UserHasAdmissionEntryToken";
import {UserHasAdmissionToken} from "../models/admission-session/UserHasAdmissionToken";
import {Page404} from "../pages/page-404";
import {TokenIsNotExpired} from "../models/admission-session/TokenIsNotExpired";
import {
  AdmissionApiClientConfiguration,
  AdmissionRestApiClientConfiguration,
} from "./useApiClientConfiguration";
import {AutoLogoutProvider} from "../contexts/AutoLogoutContext";
import {AutoLogoutWarningBar} from "../atoms/auto-logout";
import {dispatch} from "../store";
import {ErrorBoundary} from "../atoms/error-boundary";
import {ADMISSION_WELCOME_ROUTE} from "../pages/page-admission-welcome/route";

const GuardedPageWelcome = UserIsLoggedOut(PageWelcome);
const GuardedPageEnterAdmission = PageEnterAdmission;
const GuardedPageAdmissionVerifyAnswers = UserHasAdmissionEntryToken(
  TokenIsNotExpired(PageAdmissionVerifyAnswers),
);
const GuardedPageAdmissionPrelude = UserHasAdmissionToken(
  TokenIsNotExpired(PageAdmissionPrelude),
);
const GuardedPageAdmissionExperience = UserHasAdmissionToken(
  TokenIsNotExpired(PageAdmissionExperience),
);

/**
 * Component for authenticated admission routes with auto-logout functionality.
 *
 * @returns {JSX.Element} The authenticated admission routes with error boundary and auto-logout.
 */
const AuthenticatedAdmissionRoutes = () => {
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    await dispatch.admissionSession.logout();
    navigate(ADMISSION_WELCOME_ROUTE, {replace: true});
  }, [navigate]);

  return (
    <ErrorBoundary>
      <AutoLogoutProvider userType="re" onLogout={handleLogout}>
        <AutoLogoutWarningBar />
        <AdmissionApiClientConfiguration />
        <AdmissionRestApiClientConfiguration />
        <SentryRoutes>
          <Route path="enter" element={<GuardedPageEnterAdmission />} />
          <Route
            path="verify"
            element={<GuardedPageAdmissionVerifyAnswers />}
          />
          <Route path="prelude" element={<GuardedPageAdmissionPrelude />} />
          <Route
            path="explore/:admissionId"
            element={<GuardedPageAdmissionExperience />}
          />
          <Route path="*" element={<Page404 />} />
        </SentryRoutes>
      </AutoLogoutProvider>
    </ErrorBoundary>
  );
};

/**
 * Main admission routes component that handles both public and authenticated routes.
 * Routes between welcome page, expired session, and authenticated admission flows.
 *
 * @returns {JSX.Element} The admission routes component.
 */
export const AdmissionRoutes = () => (
  <SentryRoutes>
    <Route path="welcome" element={<GuardedPageWelcome />} />
    <Route path="session-expired" element={<PageExpiredSession />} />
    <Route path="*" element={<AuthenticatedAdmissionRoutes />} />
  </SentryRoutes>
);
